/**
 * Hook for mock code execution
 */

import { useState, useCallback } from "react";
import { type MockData } from "#app/lib/tutorials/parser";

export interface MockExecutionState {
  isExecuting: boolean;
  output: string | null;
  error: string | null;
  executionTime: number;
}

export function useMockExecution(mockData: MockData | null) {
  const [state, setState] = useState<MockExecutionState>({
    isExecuting: false,
    output: null,
    error: null,
    executionTime: 0,
  });

  const execute = useCallback(async () => {
    if (!mockData) {
      setState({
        isExecuting: false,
        output: null,
        error: "No mock data available",
        executionTime: 0,
      });
      return;
    }

    setState({
      isExecuting: true,
      output: null,
      error: null,
      executionTime: 0,
    });

    const startTime = Date.now();
    const executionTime = mockData.executionTime || 500;

    // Simulate execution delay
    await new Promise((resolve) => setTimeout(resolve, executionTime));

    const elapsed = Date.now() - startTime;

    if (mockData.status === "error") {
      setState({
        isExecuting: false,
        output: null,
        error: mockData.error || "Execution failed",
        executionTime: elapsed,
      });
    } else {
      setState({
        isExecuting: false,
        output: mockData.output || "",
        error: null,
        executionTime: elapsed,
      });
    }
  }, [mockData]);

  const reset = useCallback(() => {
    setState({
      isExecuting: false,
      output: null,
      error: null,
      executionTime: 0,
    });
  }, []);

  return {
    ...state,
    execute,
    reset,
  };
}
